/**
 * Mapas como visão de primeira classe, com sobreposição de dados por camadas.
 *
 * `<MapLayers label layers value onChange />` — barra de camadas liga/desliga: cada camada `{ id, label, tone?,
 * color?, count?, legend?, hint?, disabled? }` vira um botão com a cor (amostra), o nome e a contagem; `value` são os
 * ids ligados. A legenda (`legend: { label, color }[]`) das camadas ligadas aparece logo abaixo.
 * Tons prontos: 'state' (estado/problemas), 'alerts', 'vulns', 'pred' (falhas previstas), 'traffic', 'cost'; ou
 * `color` com qualquer cor CSS (de preferência um token: 'var(--info)').
 * Para lembrar por pessoa e por mapa: `const [on, setOn] = useMapLayers('k8s.map.layers', layers, ['state'])`.
 *
 * `<MapView label layers toolbar detail>{desenho}</MapView>` — contêiner que envolve um mapa existente sem reescrever
 * o desenho: barra no topo (`toolbar`, ex.: `ViewToggle`, e a barra de camadas quando `layers` é passado) e a área do
 * mapa com o painel de detalhe à direita (`detail`, normalmente um `DetailPanel` do nó clicado; no celular, gaveta).
 * `layers`: `{ items, value, onChange, label }` (as mesmas props de `MapLayers`). O desenho recebe as camadas ligadas
 * por conta própria (quem monta a tela passa `value` ao desenho).
 */
import { useI18n, type CommonKey } from '@hive/i18n';
import type { ReactNode } from 'react';
import { ListDetail } from './detail';
import { cx } from './layout';
import { useRemembered } from './prefs';

export type MapLayerTone = 'state' | 'alerts' | 'vulns' | 'pred' | 'traffic' | 'cost';

export interface MapLayer {
  id: string;
  label: ReactNode;
  tone?: MapLayerTone;
  /** Cor CSS da camada (vence o tom). */
  color?: string;
  /** Quantos itens a camada marca no mapa (ex.: 14 alertas). */
  count?: number | null;
  legend?: { label: ReactNode; color: string }[];
  /** Dica (title) do botão. */
  hint?: string;
  disabled?: boolean;
}

/** Camadas ligadas, lembradas por pessoa e por mapa; ids que deixaram de existir saem. */
export function useMapLayers(key: string, layers: MapLayer[], defaults: string[]): [string[], (v: string[]) => void] {
  const [v, set] = useRemembered<string[]>(key, defaults, (x) => Array.isArray(x) && x.every((i) => typeof i === 'string'));
  const ids = new Set(layers.map((l) => l.id));
  return [v.filter((i) => ids.has(i)), set];
}

export function MapLayers({
  label,
  layers,
  value,
  onChange,
  className,
}: {
  label: string;
  layers: MapLayer[];
  value: string[];
  onChange: (next: string[]) => void;
  className?: string;
}) {
  const { fmt } = useI18n<CommonKey>();
  const on = new Set(value);
  const legends = layers.filter((l) => on.has(l.id) && l.legend?.length);
  return (
    <div className={cx('map-layers', className)}>
      <div className="ml-bar" role="group" aria-label={label}>
        <span className="ml-title" aria-hidden="true">
          {label}
        </span>
        {layers.map((l) => (
          <button
            key={l.id}
            type="button"
            className={cx('ml-btn', on.has(l.id) && 'on')}
            aria-pressed={on.has(l.id)}
            disabled={l.disabled}
            title={l.hint}
            onClick={() => onChange(on.has(l.id) ? value.filter((x) => x !== l.id) : [...value, l.id])}
          >
            <span className={cx('ml-sw', l.tone && `ml-${l.tone}`)} style={l.color ? { background: l.color } : undefined} aria-hidden="true" />
            {l.label}
            {l.count != null && <span className="ml-count">{fmt.number(l.count)}</span>}
          </button>
        ))}
      </div>
      {legends.length > 0 && (
        <ul className="ml-legend">
          {legends.flatMap((l) =>
            (l.legend ?? []).map((g, i) => (
              <li key={`${l.id}-${i}`}>
                <span className="ml-sw" style={{ background: g.color }} aria-hidden="true" />
                {g.label}
              </li>
            )),
          )}
        </ul>
      )}
    </div>
  );
}

export function MapView({
  label,
  layers,
  toolbar,
  detail,
  children,
  className,
}: {
  label: string;
  layers?: { items: MapLayer[]; value: string[]; onChange: (next: string[]) => void; label: string };
  toolbar?: ReactNode;
  detail?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx('map-view', className)} aria-label={label}>
      {(toolbar || layers) && (
        <div className="mv-bar">
          {toolbar}
          {layers && <MapLayers label={layers.label} layers={layers.items} value={layers.value} onChange={layers.onChange} />}
        </div>
      )}
      <ListDetail className="mv-body" list={<div className="mv-map">{children}</div>} detail={detail} />
    </section>
  );
}
